/**
 * LinkApplicationPicker
 * Renders when no MoveInLink exists. Shows a searchable list of accepted leads
 * for this unit. The landlord selects one → calls bindMoveInLink → refreshes.
 */

import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { bindMoveInLink } from '../../lib/move-in.api';
import { fetchListings, fetchListingLeads } from '../../lib/leasing.api';
import type { Lead } from '../../lib/leasing.api';

interface LinkApplicationPickerProps {
  unitId: string;
  onLinked: () => void;
}

export function LinkApplicationPicker({ unitId, onLinked }: LinkApplicationPickerProps) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadLeads() {
      setIsLoading(true);
      setError(null);
      try {
        // Get listings for this unit
        const listingsResponse = await fetchListings({ unitId });
        const unitListings = listingsResponse.items;

        if (unitListings.length === 0) {
          if (!cancelled) {
            setLeads([]);
            setIsLoading(false);
          }
          return;
        }

        // Fetch accepted leads for each listing
        const allLeads: Lead[] = [];
        await Promise.all(
          unitListings.map(async (listing) => {
            try {
              const response = await fetchListingLeads(listing.id, { stage: 'closed' });
              const accepted = response.items.filter(
                (l) => l.decision === 'approved_lease_started'
              );
              allLeads.push(...accepted);
            } catch {
              // silently skip listings that fail
            }
          })
        );

        if (!cancelled) {
          setLeads(allLeads);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load applications');
          setIsLoading(false);
        }
      }
    }

    void loadLeads();
    return () => { cancelled = true; };
  }, [unitId]);

  const handleLink = async () => {
    if (!selectedLeadId) return;
    setIsLinking(true);
    setLinkError(null);
    try {
      await bindMoveInLink(unitId, selectedLeadId);
      onLinked();
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to link application');
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div data-testid="link-application-picker" className="border border-border rounded-lg p-4">
      <h3 className="text-sm font-semibold text-foreground mb-1">
        Link accepted application
      </h3>
      <p className="text-xs text-muted-foreground mb-4">
        Select an approved application to link to this move-in workflow.
      </p>

      {isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && error && (
        <p className="text-sm text-destructive mb-3" role="alert">{error}</p>
      )}

      {!isLoading && !error && leads.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No approved applications found for this unit.
        </p>
      )}

      {!isLoading && !error && leads.length > 0 && (
        <fieldset className="flex flex-col gap-2 mb-4 border-0 p-0 m-0">
          <legend className="sr-only">Accepted applications</legend>
          {leads.map((lead) => {
            const name = lead.prospectName ?? lead.prospectEmail;
            const isSelected = lead.id === selectedLeadId;
            return (
              <label
                key={lead.id}
                className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-md border text-sm transition-colors cursor-pointer focus-within:ring-2 focus-within:ring-ring ${
                  isSelected
                    ? 'bg-primary/10 border-primary text-foreground'
                    : 'border-border bg-background text-foreground hover:bg-muted/50'
                }`}
              >
                <input
                  type="radio"
                  name="link-application-lead"
                  value={lead.id}
                  checked={isSelected}
                  onChange={() => setSelectedLeadId(lead.id)}
                  className="sr-only"
                />
                <span className="font-medium">{name}</span>
                {lead.prospectEmail && name !== lead.prospectEmail && (
                  <span className="text-xs text-muted-foreground">{lead.prospectEmail}</span>
                )}
                <span className="ml-auto text-xs font-medium text-success bg-success/10 px-1.5 py-0.5 rounded">
                  Approved
                </span>
              </label>
            );
          })}
        </fieldset>
      )}

      {linkError && (
        <p className="text-sm text-destructive mb-3" role="alert">{linkError}</p>
      )}

      {!isLoading && leads.length > 0 && (
        <Button
          variant="default"
          size="sm"
          disabled={!selectedLeadId || isLinking}
          onClick={() => void handleLink()}
          aria-label="Link selected application"
          data-testid="link-application-submit"
        >
          {isLinking ? 'Linking…' : 'Link application'}
        </Button>
      )}
    </div>
  );
}
